<template>
  <v-dialog
    :value="state.open"
    max-width="440"
    persistent
    content-class="app-alert-dialog"
    transition="scale-transition"
    @keydown.esc="close"
  >
    <div class="app-alert" :class="'app-alert--' + state.type">
      <div class="app-alert-top">
        <div class="app-alert-ring">
          <div class="app-alert-icon">
            <v-icon size="38" color="white">{{ icon }}</v-icon>
          </div>
        </div>
      </div>

      <div class="app-alert-body">
        <h2 class="app-alert-title">{{ state.title || defaultTitle }}</h2>
        <span v-if="state.highlight" class="app-alert-highlight">{{ state.highlight }}</span>
        <p v-if="state.message" class="app-alert-message">{{ state.message }}</p>
      </div>

      <div class="app-alert-actions">
        <v-btn
          block
          depressed
          height="46"
          :color="buttonColor"
          class="white--text text-none font-weight-bold"
          @click="close"
        >
          {{ state.confirmText }}
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { alertState, closeAlert } from "@/plugins/appAlert";

const TYPES = {
  success: { icon: "mdi-check-bold", title: "Done!", color: "#15803d" },
  error: { icon: "mdi-close-thick", title: "Something went wrong", color: "#dc2626" },
  warning: { icon: "mdi-exclamation-thick", title: "Please check", color: "#d97706" },
  info: { icon: "mdi-information-variant", title: "Notice", color: "#2563eb" },
};

export default {
  name: "AppAlert",
  data() {
    return {
      state: alertState,
    };
  },
  computed: {
    type() {
      return TYPES[this.state.type] || TYPES.info;
    },
    icon() {
      return this.type.icon;
    },
    defaultTitle() {
      return this.type.title;
    },
    buttonColor() {
      return this.type.color;
    },
  },
  methods: {
    close() {
      closeAlert();
    },
  },
};
</script>

<style scoped>
.app-alert {
  overflow: hidden;
  border-radius: 22px;
  background: #ffffff;
  text-align: center;
  font-family: "Inter", sans-serif;
}

.app-alert-top {
  display: flex;
  justify-content: center;
  padding: 30px 0 18px;
  background: radial-gradient(circle at 50% 120%, var(--soft) 0%, #ffffff 70%);
}

.app-alert-ring {
  padding: 10px;
  border-radius: 50%;
  background: var(--soft);
  animation: app-alert-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.app-alert-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  box-shadow: 0 10px 24px var(--shadow);
}

.app-alert-body {
  padding: 0 28px;
}

.app-alert-title {
  margin: 0 0 10px;
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: #0f172a;
}

.app-alert-highlight {
  display: inline-block;
  margin-bottom: 12px;
  padding: 5px 14px;
  border-radius: 999px;
  background: var(--soft);
  color: var(--accent);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.4px;
  font-variant-numeric: tabular-nums;
}

.app-alert-message {
  margin: 0;
  font-size: 14.5px;
  line-height: 1.55;
  color: #475569;
}

.app-alert-actions {
  padding: 24px 28px 26px;
}

.app-alert--success {
  --accent: #15803d;
  --soft: rgba(21, 128, 61, 0.12);
  --shadow: rgba(21, 128, 61, 0.35);
}

.app-alert--error {
  --accent: #dc2626;
  --soft: rgba(220, 38, 38, 0.1);
  --shadow: rgba(220, 38, 38, 0.3);
}

.app-alert--warning {
  --accent: #d97706;
  --soft: rgba(245, 158, 11, 0.14);
  --shadow: rgba(217, 119, 6, 0.3);
}

.app-alert--info {
  --accent: #2563eb;
  --soft: rgba(37, 99, 235, 0.1);
  --shadow: rgba(37, 99, 235, 0.3);
}

@keyframes app-alert-pop {
  0% {
    transform: scale(0.4);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 600px) {
  .app-alert-body {
    padding: 0 20px;
  }

  .app-alert-actions {
    padding: 20px;
  }
}
</style>

<style>
.app-alert-dialog {
  border-radius: 22px !important;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3) !important;
}
</style>
